import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert
} from '@mui/material';

/**
 * Duplicate Assessment Dialog
 * 
 * Allows users to duplicate an assessment with options to copy:
 * - Answers
 * - Notes
 * - Private Notes
 * 
 * The duplicated assessment will be created in Draft status with
 * the name "Copy of [Original Name]"
 */
const DuplicateAssessmentDialog = ({ 
  open, 
  onClose, 
  assessmentId,
  assessmentName,
  onDuplicate 
}) => {
  const [copyOptions, setCopyOptions] = useState({
    copy_answers: true,
    copy_notes: true,
    copy_private_notes: false
  });
  const [duplicating, setDuplicating] = useState(false);

  const handleCheckboxChange = (option) => {
    setCopyOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      await onDuplicate(copyOptions);
      onClose();
      // Reset to defaults for next time
      setCopyOptions({
        copy_answers: true,
        copy_notes: true,
        copy_private_notes: false
      });
    } catch (error) {
      console.error('Error duplicating assessment:', error);
    } finally {
      setDuplicating(false);
    }
  };

  const handleClose = () => {
    if (!duplicating) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Duplicate Assessment
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Create a copy of: <strong>{assessmentName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The new assessment will be named "Copy of {assessmentName}" and created in Draft status.
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Select what you want to copy from the original assessment:
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={copyOptions.copy_answers}
                onChange={() => handleCheckboxChange('copy_answers')}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  Answers
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Copy all question responses (Met, Not Met, Partially Met, N/A)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox 
                checked={copyOptions.copy_notes}
                onChange={() => handleCheckboxChange('copy_notes')}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  Notes
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Copy regular notes visible to clients
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox 
                checked={copyOptions.copy_private_notes}
                onChange={() => handleCheckboxChange('copy_private_notes')}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="bold">
                  Private Notes (Internal)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Copy private assessor notes (internal use only)
                </Typography>
              </Box>
            }
          />
        </Box>

        {!copyOptions.copy_answers && !copyOptions.copy_notes && !copyOptions.copy_private_notes && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Note: You haven't selected anything to copy. The duplicate will be a blank assessment.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={duplicating}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDuplicate}
          variant="contained"
          disabled={duplicating}
          sx={{ 
            backgroundColor: '#3498db',
            '&:hover': { backgroundColor: '#2980b9' }
          }}
        >
          {duplicating ? 'Duplicating...' : 'Duplicate Assessment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateAssessmentDialog;
